import { useCallback, useEffect, useMemo, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectDeviceProfile } from "features/render";
import { classifyLayer1, classifyLayer2, isWindows } from "utils/gpuDetection";

import { buildGuidanceItems, classifySituation, isGhostNudgeEligible } from "./classifySituation";
import { sharedDialogLifecycle } from "./dialogLifecycle";
import { computeEngineResolutionFloor } from "./engineResolutionFloor";
import { ensureGuidanceSnapshot } from "./ensureGuidanceSnapshot";
import { getCachedDetectGpuRaw, getCachedWebGl2, getCachedWebGpu } from "./gpuProbeCache";
import { type GuidanceSnapshot } from "./guidanceModel";
import { setGuidanceLifecycleWriter, setGuidanceRerun, setGuidanceSnapshot } from "./guidanceStore";
import {
    defaultWatchdogConfig,
    PerformanceWatchdog,
    type WatchdogBreachEvent,
    type WatchdogWarmupEvent,
} from "./performanceWatchdog";
import { getWatchdogConfigFromUrl } from "./watchdogDevOverrides";

type WatchdogEvents = {
    warmup?: WatchdogWarmupEvent;
    breach?: WatchdogBreachEvent;
    qualityFloor?: WatchdogBreachEvent;
};

export function useGpuDetection(enabled = false) {
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();
    const deviceProfile = useAppSelector(selectDeviceProfile);

    const watchdogRef = useRef<PerformanceWatchdog>();
    const watchdogEventsRef = useRef<WatchdogEvents>({});
    const detectionSeqRef = useRef(0);
    const watchdogConfig = useMemo(() => getWatchdogConfigFromUrl(defaultWatchdogConfig), []);

    const runDetection = useCallback(async () => {
        // Probe results are stable for the whole browser session: flipping hardware
        // acceleration, the Windows per-app GPU preference or HAGS only takes effect
        // after a browser relaunch, so re-probing mid-session can never observe a
        // different result. Cache both probes (like detect-gpu below) so watchdog
        // reruns only re-classify instead of creating a new WebGL context and
        // WebGPU adapter request on every pass.
        //
        // BM-13984 telemetry / fix rate: a later `gpu_detection_classified` of
        // healthy is "settings changed + new viewer load", not a dedicated event.
        // GPU-preference changes still classify as B (integrated-gpu) on reload of
        // the same browser process — only a full quit-and-reopen can look healthy.
        // Do not add a "fixed" event for that; it is how this event should be read.
        const webgl = getCachedWebGl2();
        const webgpu = await getCachedWebGpu();
        const detectGpuRaw = await getCachedDetectGpuRaw();
        const layer1 = classifyLayer1(webgl, webgpu);
        const renderer = webgl.renderer ?? webgpu.adapterInfo?.description ?? webgpu.adapterInfo?.device;
        const layer2 = classifyLayer2({
            renderer,
            tier: deviceProfile.tier,
            detectGpu: detectGpuRaw,
            webGpuAdapterInfo: webgpu.adapterInfo,
        });

        const windows = isWindows({ renderer, webGpuAdapterInfo: webgpu.adapterInfo });
        const situation = classifySituation({
            layer1,
            layer2,
            watchdogBreach: watchdogEventsRef.current.breach,
            watchdogQualityFloor: watchdogEventsRef.current.qualityFloor,
            isWindows: windows,
        });
        const lifecycle = sharedDialogLifecycle.evaluate(situation);
        const ghostNudgePending = sharedDialogLifecycle.isGhostNudgePending(
            isGhostNudgeEligible({ isWindows: windows, layer2, situation }),
        );

        const snapshot: GuidanceSnapshot = {
            situation,
            lifecycle,
            items: buildGuidanceItems({
                isWindows: windows,
                layer1,
                layer2,
                watchdogBreach: watchdogEventsRef.current.breach,
                watchdogQualityFloor: watchdogEventsRef.current.qualityFloor,
            }),
            isWindows: windows,
            ghostNudgePending,
            renderer,
            layer1,
            layer2,
            webgl,
            webgpu,
            detectGpuRaw,
            watchdogWarmup: watchdogEventsRef.current.warmup,
            watchdogBreach: watchdogEventsRef.current.breach,
            watchdogQualityFloor: watchdogEventsRef.current.qualityFloor,
        };

        setGuidanceSnapshot(snapshot);
    }, [deviceProfile.tier]);

    const rerun = useCallback(async () => {
        await runDetection();
    }, [runDetection]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        setGuidanceLifecycleWriter(sharedDialogLifecycle);
        setGuidanceRerun(rerun);
    }, [enabled, rerun]);

    useEffect(() => {
        // Early pass so guidance (e.g. situation A) is available before a `View`
        // exists. Once a view is present the watchdog-session effect below owns
        // detection, so running here would only duplicate its initial pass.
        if (!enabled || view) {
            return;
        }

        void ensureGuidanceSnapshot(true);
    }, [enabled, view]);

    useEffect(() => {
        if (!enabled || !view) {
            return;
        }

        const sequence = ++detectionSeqRef.current;

        const run = async () => {
            // New watchdog session: stale events from a previous view/environment must not
            // keep classifying situation D or emphasizing items for the new session.
            watchdogEventsRef.current = {};
            await runDetection();
            if (sequence !== detectionSeqRef.current) {
                return;
            }

            const framerateTarget = getFramerateTarget(deviceProfile.framerateTarget);
            watchdogRef.current?.stop();
            watchdogRef.current = new PerformanceWatchdog({
                view,
                internalEventEmitter,
                framerateTarget,
                config: watchdogConfig,
                getEngineResolutionFloor: () =>
                    computeEngineResolutionFloor(view.canvas, deviceProfile.tier, deviceProfile.renderResolution),
                onWarmupSettled: async (event) => {
                    if (sequence !== detectionSeqRef.current) {
                        return;
                    }
                    watchdogEventsRef.current.warmup = event;
                    await runDetection();
                },
                onBreach: async (event) => {
                    if (sequence !== detectionSeqRef.current) {
                        return;
                    }
                    if (event.mode === "underperformance") {
                        watchdogEventsRef.current.breach = event;
                    } else {
                        watchdogEventsRef.current.qualityFloor = event;
                    }
                    await runDetection();
                },
            });
            watchdogRef.current.start();
        };

        void run();

        return () => {
            detectionSeqRef.current += 1;
            watchdogRef.current?.stop();
        };
    }, [
        deviceProfile.framerateTarget,
        deviceProfile.renderResolution,
        deviceProfile.tier,
        enabled,
        internalEventEmitter,
        runDetection,
        view,
        watchdogConfig,
    ]);
}

function getFramerateTarget(defaultTarget: number): number {
    if (!import.meta.env.DEV) {
        return defaultTarget;
    }

    const value = getNumberQueryParam("gpuWatchdogFpsTarget");
    if (value === undefined || value <= 0) {
        return defaultTarget;
    }

    return value;
}

function getNumberQueryParam(key: string): number | undefined {
    const value = new URLSearchParams(window.location.search).get(key);
    if (!value) {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}
